import {
  ArrowUpRight,
  BarChart3,
  Bell,
  Bot,
  Check,
  ChevronDown,
  CircleAlert,
  Clock3,
  Download,
  ExternalLink,
  Gauge,
  Github,
  Hash,
  LayoutDashboard,
  Link2,
  LogOut,
  ListFilter,
  LoaderCircle,
  Menu,
  Printer,
  RefreshCw,
  Search,
  Send,
  Settings,
  ShieldCheck,
  Sparkles,
  Timer,
  Trash2,
  TrendingUp,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  incidentSeverities,
  incidentStatuses,
  severityLabels,
  statusLabels,
  type DashboardSummary,
  type IncidentDetail,
  type IncidentListItem,
  type IncidentNotification,
  type IncidentReport,
  type IncidentSeverity,
  type IncidentStatus,
  type TenantRole,
  type TenantSummary,
  type ReportFilters,
} from "@scim/shared";
import { ApiError, api, type MeResponse, type MembersResponse, type SettingsResponse } from "./api";
import { formatDuration, relativeTime } from "./format";

type View = "incidents" | "reports" | "settings" | "members";

function SeverityBadge({ severity }: { severity: IncidentSeverity }) {
  return (
    <span
      className={`severity severity-${severity.toLowerCase()}`}
      title={severityLabels[severity]}
    >
      <span className="severity-dot" />
      {severity}
    </span>
  );
}

function StatusBadge({ status }: { status: IncidentStatus }) {
  return <span className={`status status-${status}`}>{statusLabels[status]}</span>;
}

function LoadingScreen() {
  return (
    <main className="center-screen" aria-live="polite">
      <div className="brand-mark">
        <Sparkles size={24} />
      </div>
      <LoaderCircle className="spin" size={24} />
      <p>インシデントを読み込んでいます</p>
    </main>
  );
}

function LoginScreen() {
  return (
    <main className="login-screen">
      <section className="login-panel">
        <div className="brand-lockup">
          <div className="brand-mark">
            <Sparkles size={22} />
          </div>
          <span>SkyCLAP Incident Manager</span>
        </div>
        <p className="eyebrow">INCIDENT COMMAND CENTER</p>
        <h1>
          障害対応の全体像を、
          <br />
          ひとつの場所に。
        </h1>
        <p className="login-copy">
          Discordで起票されたインシデントを横断検索し、初動から復旧までの状況を追跡します。
        </p>
        <a className="primary-button login-button" href="/auth/discord">
          <Bot size={19} /> Discordでログイン <ArrowUpRight size={17} />
        </a>
        <p className="login-note">
          <ShieldCheck size={15} /> 所属テナントの情報だけが表示されます
        </p>
      </section>
      <aside className="login-visual" aria-hidden="true">
        <div className="signal-grid" />
        <div className="visual-card visual-card-primary">
          <div>
            <span className="live-pulse" /> LIVE INCIDENT
          </div>
          <strong>P1 · Payment API</strong>
          <p>決済APIのタイムアウト増加</p>
          <div className="progress-line">
            <span />
          </div>
        </div>
        <div className="visual-card visual-card-secondary">
          <Check size={18} /> Discordスレッドを作成
        </div>
      </aside>
    </main>
  );
}

function NoTenantAccess({ me }: { me: MeResponse }) {
  return (
    <main className="no-access-screen">
      <section className="no-access-card">
        <div className="brand-mark">
          <ShieldCheck size={22} />
        </div>
        <p className="eyebrow">TENANT ACCESS REQUIRED</p>
        <h1>利用できるワークスペースがありません</h1>
        <p>
          SCIMへのログインは完了しています。Discordサーバーの管理者に、あなたのDiscordユーザーIDをメンバーとして追加してもらってください。
        </p>
        <div className="discord-id-card">
          <span>あなたのDiscordユーザーID</span>
          <strong>{me.user.discordUserId ?? "取得できませんでした"}</strong>
        </div>
        <div className="no-access-actions">
          <a className="primary-button" href="/">
            <RefreshCw size={15} /> 再確認
          </a>
          <a className="secondary-button" href="/auth/logout">
            <LogOut size={15} /> ログアウト
          </a>
        </div>
      </section>
    </main>
  );
}

function Sidebar({
  view,
  onView,
  mobileOpen,
  onClose,
}: {
  view: View;
  onView: (view: View) => void;
  mobileOpen: boolean;
  onClose: () => void;
}) {
  const item = (key: View, label: string, icon: React.ReactNode) => (
    <button
      className={`nav-item ${view === key ? "active" : ""}`}
      onClick={() => {
        onView(key);
        onClose();
      }}
      type="button"
    >
      {icon}
      <span>{label}</span>
    </button>
  );
  return (
    <aside className={`sidebar ${mobileOpen ? "mobile-open" : ""}`}>
      <div className="sidebar-brand">
        <div className="brand-mark small">
          <Sparkles size={18} />
        </div>
        <span title="SkyCLAP Incident Manager">SCIM</span>
        <button
          className="icon-button sidebar-close"
          onClick={onClose}
          aria-label="メニューを閉じる"
        >
          <X />
        </button>
      </div>
      <nav aria-label="メインナビゲーション">
        <p className="nav-label">OPERATIONS</p>
        {item("incidents", "障害一覧", <LayoutDashboard size={18} />)}
        {item("reports", "レポート", <BarChart3 size={18} />)}
        <p className="nav-label nav-label-spaced">WORKSPACE</p>
        {item("settings", "テナント設定", <Settings size={18} />)}
        {item("members", "メンバー", <Users size={18} />)}
      </nav>
      <div className="sidebar-status">
        <span className="status-light" />
        <div>
          <strong>All systems operational</strong>
          <small>最終確認 1分前</small>
        </div>
      </div>
    </aside>
  );
}

function Header({
  me,
  tenant,
  onTenant,
  onMenu,
  onIncident,
}: {
  me: MeResponse;
  tenant: TenantSummary;
  onTenant: (id: string) => void;
  onMenu: () => void;
  onIncident: (incidentId: string) => void;
}) {
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [notifications, setNotifications] = useState<IncidentNotification[]>([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const loadNotifications = useCallback(async () => {
    setNotificationsLoading(true);
    try {
      const result = await api.notifications(tenant.id);
      setNotifications(result.notifications);
    } catch (error) {
      console.error(error);
    } finally {
      setNotificationsLoading(false);
    }
  }, [tenant.id]);
  useEffect(() => {
    void loadNotifications();
    const timer = window.setInterval(loadNotifications, 30_000);
    return () => window.clearInterval(timer);
  }, [loadNotifications]);
  const unread = notifications.filter((item) => !item.read).length;
  const markRead = async (items: IncidentNotification[]) => {
    const keys = items.filter((item) => !item.read).map((item) => item.key);
    if (!keys.length) return;
    setNotifications((current) =>
      current.map((item) => (keys.includes(item.key) ? { ...item, read: true } : item)),
    );
    try {
      await api.markNotificationsRead(tenant.id, keys);
    } catch (error) {
      console.error(error);
      void loadNotifications();
    }
  };
  return (
    <header className="topbar">
      <button className="icon-button mobile-menu" onClick={onMenu} aria-label="メニューを開く">
        <Menu />
      </button>
      <div className="tenant-switcher">
        <div className="tenant-avatar">{tenant.name.slice(0, 1)}</div>
        <div>
          <small>WORKSPACE</small>
          <strong>{tenant.name}</strong>
        </div>
        {me.tenants.length > 1 && (
          <>
            <ChevronDown size={16} />
            <select
              aria-label="テナントを切り替える"
              value={tenant.id}
              onChange={(event) => onTenant(event.target.value)}
            >
              {me.tenants.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </>
        )}
      </div>
      <div className="header-actions">
        <div className="notification-wrap">
          <button
            className={`icon-button notification-button ${notificationOpen ? "active" : ""}`}
            aria-label={`通知${unread ? `（未読${unread}件）` : ""}`}
            aria-expanded={notificationOpen}
            onClick={() => {
              setAccountOpen(false);
              setNotificationOpen((current) => !current);
            }}
          >
            <Bell size={19} />
            {unread > 0 && <span className="notification-badge">{unread > 9 ? "9+" : unread}</span>}
          </button>
          {notificationOpen && (
            <section className="notification-panel" aria-label="通知センター">
              <div className="notification-heading">
                <div>
                  <strong>通知</strong>
                  <small>{unread ? `${unread}件の未読` : "すべて確認済み"}</small>
                </div>
                {unread > 0 && (
                  <button type="button" onClick={() => markRead(notifications)}>
                    すべて既読
                  </button>
                )}
              </div>
              <div className="notification-list">
                {notificationsLoading && notifications.length === 0 ? (
                  <div className="notification-empty">
                    <LoaderCircle className="spin" size={18} /> 読み込み中
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="notification-empty">
                    <Check size={19} /> 対応が必要な通知はありません
                  </div>
                ) : (
                  notifications.map((notification) => (
                    <button
                      type="button"
                      className={`notification-item notification-${notification.level} ${notification.read ? "is-read" : ""}`}
                      key={notification.key}
                      onClick={() => {
                        void markRead([notification]);
                        setNotificationOpen(false);
                        onIncident(notification.incidentId);
                      }}
                    >
                      <span className="notification-level" />
                      <span className="notification-copy">
                        <small>{notification.ticketKey}</small>
                        <strong>{notification.title}</strong>
                        <span>{notification.message}</span>
                      </span>
                      {!notification.read && <span className="unread-dot" />}
                    </button>
                  ))
                )}
              </div>
              <footer>30秒ごとに最新状態へ更新</footer>
            </section>
          )}
        </div>
        <div className="account-wrap">
          <button
            type="button"
            className="account-trigger"
            aria-label="アカウントメニュー"
            aria-expanded={accountOpen}
            onClick={() => {
              setNotificationOpen(false);
              setAccountOpen((current) => !current);
            }}
          >
            <span className="user-avatar">{me.user.displayName.slice(0, 1)}</span>
            <span className="user-meta">
              <strong>{me.user.displayName}</strong>
              <small>{tenant.role.replace("_", " ")}</small>
            </span>
            <ChevronDown size={14} />
          </button>
          {accountOpen && (
            <section className="account-menu" aria-label="アカウント">
              <div>
                <span className="user-avatar">{me.user.displayName.slice(0, 1)}</span>
                <p>
                  <strong>{me.user.displayName}</strong>
                  <small>Discordでログイン済み</small>
                </p>
              </div>
              <a href="/auth/logout">
                <LogOut size={15} /> ログアウト
              </a>
            </section>
          )}
        </div>
      </div>
    </header>
  );
}

function MetricCard({
  label,
  value,
  note,
  tone,
  icon,
}: {
  label: string;
  value: string | number;
  note: string;
  tone: string;
  icon: React.ReactNode;
}) {
  return (
    <article className={`metric-card metric-${tone}`}>
      <div className="metric-heading">
        <span>{label}</span>
        <div className="metric-icon">{icon}</div>
      </div>
      <strong>{value}</strong>
      <small>{note}</small>
    </article>
  );
}

function IncidentTable({
  incidents,
  onSelect,
}: {
  incidents: IncidentListItem[];
  onSelect: (id: string) => void;
}) {
  if (incidents.length === 0) {
    return (
      <div className="empty-state">
        <Search size={28} />
        <h3>条件に一致する障害はありません</h3>
        <p>フィルターを変更してもう一度お試しください。</p>
      </div>
    );
  }
  return (
    <div className="table-scroll">
      <table>
        <thead>
          <tr>
            <th>重要度</th>
            <th>チケット / 件名</th>
            <th>ステータス</th>
            <th>サービス</th>
            <th>担当者</th>
            <th>発生</th>
            <th>
              <span className="sr-only">詳細</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {incidents.map((incident) => (
            <tr key={incident.id} onClick={() => onSelect(incident.id)}>
              <td>
                <SeverityBadge severity={incident.severity} />
              </td>
              <td>
                <button
                  className="incident-title"
                  type="button"
                  onClick={() => onSelect(incident.id)}
                >
                  <small>{incident.ticketKey}</small>
                  <strong>{incident.title}</strong>
                </button>
              </td>
              <td>
                <StatusBadge status={incident.status} />
              </td>
              <td>
                <span className="service-name">{incident.service}</span>
              </td>
              <td>
                {incident.assigneeName ? (
                  <span className="assignee">
                    <span>{incident.assigneeName.slice(0, 1)}</span>
                    {incident.assigneeName}
                  </span>
                ) : (
                  <span className="muted">未割当</span>
                )}
              </td>
              <td>
                <span className="time-cell">
                  {relativeTime(incident.occurredAt)}
                  <small>
                    {new Date(incident.occurredAt).toLocaleTimeString("ja-JP", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </small>
                </span>
              </td>
              <td>
                <button
                  className="row-open"
                  type="button"
                  aria-label={`${incident.ticketKey}を開く`}
                >
                  <ArrowUpRight size={17} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DetailDrawer({
  tenantId,
  incidentId,
  onClose,
  onUpdated,
}: {
  tenantId: string;
  incidentId: string | null;
  onClose: () => void;
  onUpdated: () => void;
}) {
  const [incident, setIncident] = useState<IncidentDetail | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setIncident(null);
    if (!incidentId) return;
    api
      .incident(tenantId, incidentId)
      .then((result) => setIncident(result.incident))
      .catch(console.error);
  }, [tenantId, incidentId]);
  const updateStatus = async (status: IncidentStatus) => {
    if (!incident) return;
    setBusy(true);
    try {
      const result = await api.updateIncident(tenantId, incident.id, { status });
      setIncident(result.incident);
      onUpdated();
    } finally {
      setBusy(false);
    }
  };
  const saveReview = async () => {
    if (!incident) return;
    setBusy(true);
    try {
      const result = await api.updateIncident(tenantId, incident.id, {
        rootCause: incident.rootCause || null,
        recurrenceKey: incident.recurrenceKey || null,
        postmortemStatus: incident.postmortemStatus,
        postmortemUrl: incident.postmortemUrl || null,
      });
      setIncident(result.incident);
      onUpdated();
    } finally {
      setBusy(false);
    }
  };
  if (!incidentId) return null;
  return (
    <div
      className="drawer-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.currentTarget === event.target) onClose();
      }}
    >
      <aside className="detail-drawer" aria-label="障害詳細">
        <div className="drawer-top">
          <div>
            {incident && (
              <>
                <SeverityBadge severity={incident.severity} />
                <span className="ticket-key">{incident.ticketKey}</span>
              </>
            )}
          </div>
          <button className="icon-button" onClick={onClose} aria-label="詳細を閉じる">
            <X />
          </button>
        </div>
        {!incident ? (
          <div className="drawer-loading">
            <LoaderCircle className="spin" />
          </div>
        ) : (
          <>
            <h2>{incident.title}</h2>
            <p className="drawer-impact">{incident.impact}</p>
            <div className="drawer-links">
              {incident.threadUrl && (
                <a href={incident.threadUrl} target="_blank" rel="noreferrer">
                  <Hash size={16} /> Discordスレッド <ExternalLink size={14} />
                </a>
              )}
              {incident.ticketUrl && (
                <a href={incident.ticketUrl}>
                  <CircleAlert size={16} /> チケット <ExternalLink size={14} />
                </a>
              )}
            </div>
            <section className="detail-section">
              <h3>対応状況</h3>
              <div className="status-actions">
                {incidentStatuses.map((status) => (
                  <button
                    key={status}
                    type="button"
                    disabled={busy || status === incident.status}
                    className={status === incident.status ? "selected" : ""}
                    onClick={() => updateStatus(status)}
                  >
                    {statusLabels[status]}
                  </button>
                ))}
              </div>
            </section>
            <section className="detail-section">
              <h3>概要</h3>
              <dl className="detail-grid">
                <div>
                  <dt>サービス</dt>
                  <dd>{incident.service}</dd>
                </div>
                <div>
                  <dt>担当者</dt>
                  <dd>{incident.assigneeName ?? "未割当"}</dd>
                </div>
                <div>
                  <dt>報告者</dt>
                  <dd>{incident.reporterName}</dd>
                </div>
                <div>
                  <dt>発生時刻</dt>
                  <dd>{new Date(incident.occurredAt).toLocaleString("ja-JP")}</dd>
                </div>
              </dl>
              {incident.details && <p className="detail-notes">{incident.details}</p>}
            </section>
            <section className="detail-section timeline">
              <h3>振り返り・再発防止</h3>
              <div className="review-fields">
                <label>
                  根本原因
                  <textarea
                    value={incident.rootCause ?? ""}
                    onChange={(event) =>
                      setIncident({ ...incident, rootCause: event.target.value })
                    }
                    placeholder="判明した根本原因を入力"
                  />
                </label>
                <label>
                  再発グループキー
                  <input
                    value={incident.recurrenceKey ?? ""}
                    onChange={(event) =>
                      setIncident({ ...incident, recurrenceKey: event.target.value })
                    }
                    placeholder="例: payment-db-pool"
                  />
                </label>
                <label>
                  ポストモーテム状態
                  <select
                    value={incident.postmortemStatus}
                    onChange={(event) =>
                      setIncident({
                        ...incident,
                        postmortemStatus: event.target.value as IncidentDetail["postmortemStatus"],
                      })
                    }
                  >
                    <option value="not_required">不要</option>
                    <option value="pending">未着手</option>
                    <option value="draft">作成中</option>
                    <option value="published">公開済み</option>
                  </select>
                </label>
                <label>
                  ポストモーテムURL
                  <input
                    type="url"
                    value={incident.postmortemUrl ?? ""}
                    onChange={(event) =>
                      setIncident({ ...incident, postmortemUrl: event.target.value })
                    }
                    placeholder="https://..."
                  />
                </label>
                <button
                  className="primary-button"
                  type="button"
                  disabled={busy}
                  onClick={saveReview}
                >
                  {busy ? <LoaderCircle className="spin" size={16} /> : <Check size={16} />}{" "}
                  振り返りを保存
                </button>
              </div>
            </section>
            <section className="detail-section timeline">
              <h3>タイムライン</h3>
              {incident.events.map((event) => (
                <div className="timeline-event" key={event.id}>
                  <span />
                  <div>
                    <strong>{event.message}</strong>
                    <small>
                      {event.actorName ?? "System"} · {relativeTime(event.createdAt)}
                    </small>
                  </div>
                </div>
              ))}
            </section>
          </>
        )}
      </aside>
    </div>
  );
}

function reportFiltersFromLocation(): ReportFilters {
  const params = new URLSearchParams(window.location.search);
  const preset = params.get("preset");
  const to = params.get("to") ?? new Date().toISOString();
  const defaultFrom = new Date(Date.now() - 30 * 86_400_000).toISOString();
  return {
    preset: ["7d", "30d", "90d", "12m", "custom"].includes(preset ?? "")
      ? (preset as ReportFilters["preset"])
      : "30d",
    from: params.get("from") ?? defaultFrom,
    to,
    service: params.get("service") ?? "all",
    severity: (params.get("severity") as ReportFilters["severity"]) ?? "all",
  };
}

function DistributionBars({
  items,
  empty = "対象データがありません",
}: {
  items: Array<{ key: string; label: string; count: number }>;
  empty?: string;
}) {
  const max = Math.max(1, ...items.map((item) => item.count));
  if (items.length === 0) return <p className="empty-copy">{empty}</p>;
  return (
    <div className="distribution-list">
      {items.map((item) => (
        <div className="distribution-row" key={item.key}>
          <div>
            <span>{item.label}</span>
            <strong>{item.count}件</strong>
          </div>
          <div className="distribution-track">
            <span style={{ width: `${Math.max(2, (item.count / max) * 100)}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function ReportsView({ tenant }: { tenant: TenantSummary }) {
  const [filters, setFilters] = useState<ReportFilters>(reportFiltersFromLocation);
  const [report, setReport] = useState<IncidentReport | null>(null);
  const [services, setServices] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [shareStatus, setShareStatus] = useState<string | null>(null);
  const [snapshots, setSnapshots] = useState<
    Array<{
      id: string;
      reportType: "weekly" | "monthly" | "manual";
      periodKey: string;
      createdAt: string;
    }>
  >([]);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [result, history] = await Promise.all([
        api.report(tenant.id, filters),
        api.reportSnapshots(tenant.id),
      ]);
      setReport(result.report);
      setServices((current) => [...new Set([...current, ...result.report.services])].sort());
      setSnapshots(history.snapshots);
    } finally {
      setLoading(false);
    }
  }, [tenant.id, filters]);
  useEffect(() => void load(), [load]);
  const updatePreset = (preset: ReportFilters["preset"]) => {
    const to = new Date();
    const from = new Date(to);
    if (preset === "12m") from.setFullYear(from.getFullYear() - 1);
    else if (preset !== "custom") from.setDate(from.getDate() - Number.parseInt(preset, 10));
    setFilters({ ...filters, preset, from: from.toISOString(), to: to.toISOString() });
  };
  const setDate = (key: "from" | "to", value: string) => {
    if (!value) return;
    const date = new Date(`${value}T${key === "to" ? "23:59:59.999" : "00:00:00.000"}`);
    setFilters({ ...filters, preset: "custom", [key]: date.toISOString() });
  };
  const share = async () => {
    setShareStatus("共有リンクを作成中…");
    try {
      const result = await api.shareReport(tenant.id, filters);
      await navigator.clipboard.writeText(result.url);
      setShareStatus(
        `共有リンクをコピーしました（${new Date(result.expiresAt).toLocaleDateString("ja-JP")}まで）`,
      );
    } catch (error) {
      setShareStatus(
        error instanceof ApiError && error.status === 403
          ? "共有リンクの作成にはManager権限が必要です"
          : "共有リンクを作成できませんでした",
      );
    }
  };
  const deliver = async () => {
    setShareStatus("Discordへ投稿中…");
    try {
      await api.deliverReport(tenant.id, filters);
      setShareStatus("現在のレポートをDiscordへ投稿しました");
      const history = await api.reportSnapshots(tenant.id);
      setSnapshots(history.snapshots);
    } catch (error) {
      setShareStatus(
        error instanceof ApiError && error.message === "report_channel_not_configured"
          ? "テナント設定でレポート投稿先チャンネルを設定してください"
          : "Discordへ投稿できませんでした",
      );
    }
  };
  const trendMax = Math.max(1, ...(report?.trend.map((point) => point.count) ?? []));
  const totalSlaViolations = report?.sla.reduce((sum, item) => sum + item.violations, 0) ?? 0;
  return (
    <main className={`content reports-page ${loading ? "is-loading" : ""}`}>
      <div className="page-heading reports-heading">
        <div>
          <p className="eyebrow">INCIDENT ANALYTICS</p>
          <h1>レポート</h1>
          <p>障害傾向と対応品質を可視化し、継続的な改善につなげます。</p>
        </div>
        <div className="report-actions no-print">
          <a className="secondary-button" href={api.reportCsvUrl(tenant.id, filters)} download>
            <Download size={16} /> CSV
          </a>
          <button className="secondary-button" type="button" onClick={share}>
            <Link2 size={16} /> 共有
          </button>
          <button className="secondary-button" type="button" onClick={deliver}>
            <Send size={16} /> Discordへ投稿
          </button>
          <button className="primary-button" type="button" onClick={() => window.print()}>
            <Printer size={16} /> PDF / 印刷
          </button>
        </div>
      </div>
      <section className="report-filter-panel no-print" aria-label="レポート条件">
        <div className="preset-group">
          {(["7d", "30d", "90d", "12m", "custom"] as const).map((preset) => (
            <button
              type="button"
              key={preset}
              className={filters.preset === preset ? "active" : ""}
              onClick={() => updatePreset(preset)}
            >
              {
                { "7d": "7日", "30d": "30日", "90d": "90日", "12m": "12か月", custom: "期間指定" }[
                  preset
                ]
              }
            </button>
          ))}
        </div>
        {filters.preset === "custom" && (
          <div className="date-range">
            <input
              aria-label="開始日"
              type="date"
              value={filters.from.slice(0, 10)}
              onChange={(event) => setDate("from", event.target.value)}
            />
            <span>〜</span>
            <input
              aria-label="終了日"
              type="date"
              value={filters.to.slice(0, 10)}
              onChange={(event) => setDate("to", event.target.value)}
            />
          </div>
        )}
        <select
          aria-label="サービスで絞り込み"
          value={filters.service}
          onChange={(event) => setFilters({ ...filters, service: event.target.value })}
        >
          <option value="all">すべてのサービス</option>
          {services.map((service) => (
            <option value={service} key={service}>
              {service}
            </option>
          ))}
        </select>
        <select
          aria-label="レポートの重要度"
          value={filters.severity}
          onChange={(event) =>
            setFilters({ ...filters, severity: event.target.value as ReportFilters["severity"] })
          }
        >
          <option value="all">すべての重要度</option>
          {incidentSeverities.map((severity) => (
            <option key={severity}>{severity}</option>
          ))}
        </select>
        <button className="icon-button" type="button" onClick={load} aria-label="再読み込み">
          <RefreshCw size={17} />
        </button>
      </section>
      {shareStatus && <p className="share-status no-print">{shareStatus}</p>}
      {!report ? (
        <div className="page-loading">
          <LoaderCircle className="spin" /> レポートを集計しています
        </div>
      ) : (
        <>
          <section className="metrics-grid report-metrics" aria-label="レポートサマリー">
            <MetricCard
              label="障害件数"
              value={report.totals.incidents}
              note={`未解決 ${report.totals.open}件`}
              tone="neutral"
              icon={<TrendingUp size={20} />}
            />
            <MetricCard
              label="MTTA"
              value={formatDuration(report.totals.mttaMinutes)}
              note="起票から対応開始まで"
              tone="purple"
              icon={<Timer size={20} />}
            />
            <MetricCard
              label="MTTR"
              value={formatDuration(report.totals.mttrMinutes)}
              note="発生から復旧まで"
              tone="success"
              icon={<Gauge size={20} />}
            />
            <MetricCard
              label="SLO達成率"
              value={
                report.totals.sloCompliancePercent === null
                  ? "—"
                  : `${report.totals.sloCompliancePercent}%`
              }
              note={`SLA違反 ${totalSlaViolations}件`}
              tone={totalSlaViolations ? "critical" : "success"}
              icon={<ShieldCheck size={20} />}
            />
          </section>
          <section className="report-grid report-grid-main">
            <article className="report-card trend-card">
              <div className="report-card-heading">
                <div>
                  <h2>障害件数の推移</h2>
                  <p>発生件数と復旧件数</p>
                </div>
              </div>
              <div className="trend-chart" role="img" aria-label="障害件数の推移">
                {report.trend.map((point) => (
                  <div
                    className="trend-column"
                    key={point.period}
                    title={`${point.label}: ${point.count}件`}
                  >
                    <div className="trend-bars">
                      <span
                        className="trend-resolved"
                        style={{
                          height: point.resolved
                            ? `${Math.max(4, (point.resolved / trendMax) * 100)}%`
                            : 0,
                        }}
                      />
                      <span
                        className="trend-total"
                        style={{
                          height: point.count
                            ? `${Math.max(4, (point.count / trendMax) * 100)}%`
                            : 0,
                        }}
                      />
                    </div>
                    <small>{point.label}</small>
                  </div>
                ))}
              </div>
              <div className="chart-legend">
                <span className="total" /> 発生 <span className="resolved" /> 復旧
              </div>
            </article>
            <article className="report-card">
              <div className="report-card-heading">
                <div>
                  <h2>サービス別</h2>
                  <p>障害が集中している領域</p>
                </div>
              </div>
              <DistributionBars items={report.byService} />
            </article>
          </section>
          <section className="report-grid report-grid-three">
            <article className="report-card">
              <div className="report-card-heading">
                <div>
                  <h2>重要度別</h2>
                </div>
              </div>
              <DistributionBars items={report.bySeverity} />
            </article>
            <article className="report-card">
              <div className="report-card-heading">
                <div>
                  <h2>ステータス別</h2>
                </div>
              </div>
              <DistributionBars items={report.byStatus} />
            </article>
            <article className="report-card">
              <div className="report-card-heading">
                <div>
                  <h2>担当者別</h2>
                </div>
              </div>
              <DistributionBars items={report.byAssignee} />
            </article>
          </section>
          <section className="report-grid report-grid-three quality-grid">
            <article className="report-card quality-card">
              <h2>GitHub Issues</h2>
              <strong>
                {report.github.closed}
                <small> / {report.github.linked} 完了</small>
              </strong>
              <p>
                Open {report.github.open} · 状態不明 {report.github.unknown}
              </p>
            </article>
            <article className="report-card quality-card">
              <h2>ポストモーテム</h2>
              <strong>
                {report.postmortems.published}
                <small> / {report.postmortems.required} 公開</small>
              </strong>
              <p>未完了 {report.postmortems.pending}件</p>
            </article>
            <article className="report-card quality-card">
              <h2>再発候補</h2>
              <strong>
                {report.recurrences.length}
                <small> グループ</small>
              </strong>
              <p>原因キー・類似件名から判定</p>
            </article>
          </section>
          <section className="report-grid report-grid-main">
            <article className="report-card">
              <div className="report-card-heading">
                <div>
                  <h2>SLA / SLO</h2>
                  <p>重要度別の復旧目標</p>
                </div>
              </div>
              <div className="sla-table">
                {report.sla.map((item) => (
                  <div key={item.severity}>
                    <SeverityBadge severity={item.severity} />
                    <span>{item.targetHours}時間以内</span>
                    <strong>
                      {item.compliancePercent === null ? "—" : `${item.compliancePercent}%`}
                    </strong>
                    <small>{item.violations}件違反</small>
                  </div>
                ))}
              </div>
            </article>
            <article className="report-card">
              <div className="report-card-heading">
                <div>
                  <h2>再発・頻出原因</h2>
                  <p>2回以上発生したパターン</p>
                </div>
              </div>
              {report.recurrences.length ? (
                <div className="recurrence-list">
                  {report.recurrences.map((item) => (
                    <div key={item.key}>
                      <strong>{item.key}</strong>
                      <span>
                        {item.count}回 · {item.services.join(", ")}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="empty-copy">再発候補はありません。</p>
              )}
            </article>
          </section>
          <section className="report-card report-table-card">
            <div className="report-card-heading">
              <div>
                <h2>長期化している障害</h2>
                <p>未解決のまま24時間以上経過</p>
              </div>
            </div>
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>重要度</th>
                    <th>チケット / 件名</th>
                    <th>サービス</th>
                    <th>状態</th>
                    <th>経過</th>
                  </tr>
                </thead>
                <tbody>
                  {report.longRunning.length ? (
                    report.longRunning.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <SeverityBadge severity={item.severity} />
                        </td>
                        <td>
                          <strong>{item.ticketKey}</strong>
                          <br />
                          {item.title}
                        </td>
                        <td>{item.service}</td>
                        <td>
                          <StatusBadge status={item.status} />
                        </td>
                        <td>{item.ageHours}時間</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5}>長期化している障害はありません。</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
          <section className="report-card snapshot-card no-print">
            <div className="report-card-heading">
              <div>
                <h2>自動生成履歴</h2>
                <p>週次・月次レポートのスナップショット</p>
              </div>
            </div>
            {snapshots.length ? (
              <div className="snapshot-list">
                {snapshots.map((item) => (
                  <div key={item.id}>
                    <span>
                      {item.reportType === "weekly"
                        ? "週次"
                        : item.reportType === "monthly"
                          ? "月次"
                          : "手動"}
                    </span>
                    <strong>{item.periodKey}</strong>
                    <small>{new Date(item.createdAt).toLocaleString("ja-JP")}</small>
                  </div>
                ))}
              </div>
            ) : (
              <p className="empty-copy">自動生成されたレポートはまだありません。</p>
            )}
          </section>
        </>
      )}
    </main>
  );
}

const memberRoleLabels: Record<TenantRole, string> = {
  viewer: "閲覧者",
  responder: "対応者",
  manager: "管理者",
  tenant_admin: "テナント管理者",
};

function MembersView({ tenant, currentUserId }: { tenant: TenantSummary; currentUserId: string }) {
  const [data, setData] = useState<MembersResponse | null>(null);
  const [discordUserId, setDiscordUserId] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState<TenantRole>("viewer");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setData(await api.members(tenant.id));
  }, [tenant.id]);

  useEffect(() => {
    setData(null);
    load().catch(console.error);
  }, [load]);

  const explainError = (memberError: unknown) => {
    if (memberError instanceof ApiError && memberError.message === "member_already_exists") {
      return "このユーザーはすでにメンバーです。";
    }
    if (memberError instanceof ApiError && memberError.message === "last_tenant_admin") {
      return "最後のテナント管理者は降格・削除できません。";
    }
    if (memberError instanceof ApiError && memberError.message === "invalid_request") {
      return "DiscordユーザーIDと権限を確認してください。";
    }
    return "メンバー情報を更新できませんでした。";
  };

  const invite = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.inviteMember(tenant.id, { discordUserId, displayName, role });
      setDiscordUserId("");
      setDisplayName("");
      setRole("viewer");
      await load();
    } catch (inviteError) {
      setError(explainError(inviteError));
    } finally {
      setBusy(false);
    }
  };

  const updateRole = async (userId: string, nextRole: TenantRole) => {
    setBusy(true);
    setError(null);
    try {
      await api.updateMemberRole(tenant.id, userId, nextRole);
      await load();
    } catch (roleError) {
      setError(explainError(roleError));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (userId: string, name: string) => {
    if (!window.confirm(`${name} をこのワークスペースから削除しますか？`)) return;
    setBusy(true);
    setError(null);
    try {
      await api.removeMember(tenant.id, userId);
      await load();
    } catch (removeError) {
      setError(explainError(removeError));
    } finally {
      setBusy(false);
    }
  };

  if (!data) {
    return (
      <div className="page-loading">
        <LoaderCircle className="spin" /> メンバーを読み込んでいます
      </div>
    );
  }

  return (
    <main className="content members-page">
      <div className="page-heading">
        <div>
          <p className="eyebrow">WORKSPACE ACCESS</p>
          <h1>メンバー</h1>
          <p>DiscordユーザーごとにSCIMコンソールのアクセス権限を管理します。</p>
        </div>
      </div>

      {data.canManage && (
        <section className="member-invite-card">
          <div className="settings-card-heading">
            <span className="settings-icon">
              <UserPlus size={18} />
            </span>
            <div>
              <h2>メンバーを追加</h2>
              <p>DiscordユーザーIDを登録すると、対象ユーザーはOAuthログイン後に利用できます。</p>
            </div>
          </div>
          <form className="member-invite-form" onSubmit={invite}>
            <label>
              DiscordユーザーID
              <input
                inputMode="numeric"
                pattern="[0-9]{5,30}"
                required
                value={discordUserId}
                onChange={(event) => setDiscordUserId(event.target.value.trim())}
                placeholder="123456789012345678"
              />
            </label>
            <label>
              表示名（任意）
              <input
                maxLength={100}
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                placeholder="初回ログイン時にDiscord名へ更新"
              />
            </label>
            <label>
              権限
              <select value={role} onChange={(event) => setRole(event.target.value as TenantRole)}>
                {Object.entries(memberRoleLabels).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <button className="primary-button" type="submit" disabled={busy}>
              <UserPlus size={15} /> 追加
            </button>
          </form>
          <p className="member-help">
            Discordの「ユーザー設定 → 詳細設定 →
            開発者モード」を有効にし、対象ユーザーを右クリックして「ユーザーIDをコピー」します。
          </p>
        </section>
      )}

      <section className="member-list-card" aria-label="ワークスペースメンバー">
        <div className="member-list-heading">
          <strong>{data.members.length}人のメンバー</strong>
          {!data.canManage && <small>閲覧のみ</small>}
        </div>
        <div className="member-list">
          {data.members.map((member) => (
            <div className="member-row" key={member.id}>
              <span className="member-avatar">{member.displayName.slice(0, 1)}</span>
              <div className="member-identity">
                <strong>
                  {member.displayName} {member.id === currentUserId && <small>あなた</small>}
                </strong>
                <span>{member.discordUserId ?? "Discord未連携"}</span>
              </div>
              <span
                className={`member-login-state ${member.hasLoggedIn || member.id === currentUserId ? "active" : ""}`}
              >
                {member.hasLoggedIn || member.id === currentUserId ? "ログイン済み" : "招待済み"}
              </span>
              {data.canManage ? (
                <select
                  aria-label={`${member.displayName}の権限`}
                  value={member.role}
                  disabled={busy}
                  onChange={(event) => void updateRole(member.id, event.target.value as TenantRole)}
                >
                  {Object.entries(memberRoleLabels).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="member-role">{memberRoleLabels[member.role]}</span>
              )}
              {data.canManage && (
                <button
                  type="button"
                  className="member-remove"
                  aria-label={`${member.displayName}を削除`}
                  disabled={busy}
                  onClick={() => void remove(member.id, member.displayName)}
                >
                  <Trash2 size={15} />
                </button>
              )}
            </div>
          ))}
        </div>
      </section>
      {error && <p className="settings-error">{error}</p>}
    </main>
  );
}

function SettingsView({ tenant }: { tenant: TenantSummary }) {
  const [settings, setSettings] = useState<SettingsResponse | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [githubToken, setGithubToken] = useState("");
  useEffect(() => {
    api.settings(tenant.id).then(setSettings).catch(console.error);
  }, [tenant.id]);
  if (!settings)
    return (
      <div className="page-loading">
        <LoaderCircle className="spin" /> 設定を読み込んでいます
      </div>
    );
  const guild = settings.guilds[0];
  const github = settings.github ?? {
    configured: false,
    owner: "",
    repository: "",
    projectOwner: null,
    projectNumber: null,
    labels: [],
    assignees: [],
  };
  const updateTenant = (key: keyof SettingsResponse["tenant"], value: string) =>
    setSettings({ ...settings, tenant: { ...settings.tenant, [key]: value } });
  const roleIds = (value: string) =>
    value
      .split(/[\s,]+/)
      .map((item) => item.trim())
      .filter(Boolean);
  const updateGuildRoles = (key: "allowedRoleIds" | "p1RoleIds", value: string) => {
    if (!guild) return;
    setSettings({
      ...settings,
      guilds: [{ ...guild, [key]: roleIds(value) }, ...settings.guilds.slice(1)],
    });
  };
  const updateGithub = <Key extends keyof typeof github>(key: Key, value: (typeof github)[Key]) =>
    setSettings({ ...settings, github: { ...github, [key]: value } });
  const save = async () => {
    if (!guild) return;
    setBusy(true);
    setSaved(false);
    setError(null);
    try {
      await api.updateSettings(tenant.id, {
        guildId: guild.guildId,
        name: settings.tenant.name,
        timezone: settings.tenant.timezone,
        incidentChannelId: guild.incidentChannelId,
        ticketProvider: settings.tenant.ticketProvider,
        ticketProject: settings.tenant.ticketProject,
        reportChannelId: settings.tenant.reportChannelId,
        weeklyReportsEnabled: settings.tenant.weeklyReportsEnabled,
        monthlyReportsEnabled: settings.tenant.monthlyReportsEnabled,
        slaHours: settings.tenant.slaHours,
        github:
          settings.tenant.ticketProvider === "github"
            ? {
                token: githubToken,
                owner: github.owner,
                repository: github.repository,
                projectOwner: github.projectOwner,
                projectNumber: github.projectNumber,
                labels: github.labels,
                assignees: github.assignees,
              }
            : null,
        allowedRoleIds: guild.allowedRoleIds,
        p1RoleIds: guild.p1RoleIds,
      });
      setSaved(true);
    } catch (saveError) {
      if (saveError instanceof ApiError && saveError.message === "github_token_required") {
        setError("GitHub fine-grained tokenを入力してください。");
      } else if (
        saveError instanceof ApiError &&
        saveError.message === "github_connection_failed"
      ) {
        setError(
          "GitHubへの接続に失敗しました。権限、リポジトリ、Projects番号を確認してください。",
        );
      } else {
        setError("設定を保存できませんでした。入力内容を確認してください。");
      }
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="content settings-page">
      <div className="page-heading">
        <div>
          <p className="eyebrow">WORKSPACE SETTINGS</p>
          <h1>テナント設定</h1>
          <p>Discordとチケット管理の接続先を管理します。</p>
        </div>
        <button className="primary-button" onClick={save} disabled={busy || !guild}>
          {busy ? <LoaderCircle className="spin" size={17} /> : <Check size={17} />}
          {saved ? "保存しました" : "変更を保存"}
        </button>
      </div>
      <div className="settings-layout">
        <section className="settings-card">
          <div className="settings-card-heading">
            <div className="settings-icon">
              <Users size={19} />
            </div>
            <div>
              <h2>基本情報</h2>
              <p>コンソール全体で使用する名称と時刻です。</p>
            </div>
          </div>
          <div className="form-grid">
            <label>
              テナント名
              <input
                value={settings.tenant.name}
                onChange={(e) => updateTenant("name", e.target.value)}
              />
            </label>
            <label>
              タイムゾーン
              <input
                value={settings.tenant.timezone}
                onChange={(e) => updateTenant("timezone", e.target.value)}
              />
            </label>
          </div>
        </section>
        <section className="settings-card">
          <div className="settings-card-heading">
            <div className="settings-icon purple">
              <BarChart3 size={19} />
            </div>
            <div>
              <h2>レポートとSLA</h2>
              <p>復旧目標とDiscordへの定期サマリー配信を設定します。</p>
            </div>
          </div>
          <div className="form-grid">
            <label>
              レポート投稿先チャンネルID
              <input
                value={settings.tenant.reportChannelId ?? ""}
                onChange={(event) =>
                  setSettings({
                    ...settings,
                    tenant: {
                      ...settings.tenant,
                      reportChannelId: event.target.value.trim() || null,
                    },
                  })
                }
                placeholder={guild?.incidentChannelId ?? "DiscordチャンネルID"}
              />
            </label>
            <div className="toggle-fields">
              <label>
                <input
                  type="checkbox"
                  checked={settings.tenant.weeklyReportsEnabled}
                  onChange={(event) =>
                    setSettings({
                      ...settings,
                      tenant: { ...settings.tenant, weeklyReportsEnabled: event.target.checked },
                    })
                  }
                />
                毎週月曜9時に週次レポートを投稿
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={settings.tenant.monthlyReportsEnabled}
                  onChange={(event) =>
                    setSettings({
                      ...settings,
                      tenant: { ...settings.tenant, monthlyReportsEnabled: event.target.checked },
                    })
                  }
                />
                毎月1日9時に月次レポートを投稿
              </label>
            </div>
          </div>
          <div className="sla-settings">
            {incidentSeverities.map((severity) => (
              <label key={severity}>
                <span>{severity} 復旧目標</span>
                <input
                  type="number"
                  min="0.1"
                  step="0.1"
                  value={settings.tenant.slaHours[severity]}
                  onChange={(event) =>
                    setSettings({
                      ...settings,
                      tenant: {
                        ...settings.tenant,
                        slaHours: {
                          ...settings.tenant.slaHours,
                          [severity]: Number(event.target.value),
                        },
                      },
                    })
                  }
                />
                <small>時間</small>
              </label>
            ))}
          </div>
          <div className="info-banner">
            <Clock3 size={18} />
            <div>
              <strong>テナントのタイムゾーンを使用</strong>
              <p>{settings.tenant.timezone}基準でスナップショットを生成し、Discordへ投稿します。</p>
            </div>
          </div>
        </section>
        <section className="settings-card">
          <div className="settings-card-heading">
            <div className="settings-icon purple">
              <Hash size={19} />
            </div>
            <div>
              <h2>Discord</h2>
              <p>インシデントスレッドの作成先です。</p>
            </div>
          </div>
          {guild ? (
            <div className="form-grid">
              <label>
                Discordサーバー
                <input value={guild.guildName} disabled />
              </label>
              <label>
                親チャンネルID
                <input
                  value={guild.incidentChannelId ?? ""}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      guilds: [
                        { ...guild, incidentChannelId: e.target.value },
                        ...settings.guilds.slice(1),
                      ],
                    })
                  }
                />
              </label>
              <label>
                起票を許可するロールID
                <input
                  value={guild.allowedRoleIds.join(", ")}
                  onChange={(e) => updateGuildRoles("allowedRoleIds", e.target.value)}
                  placeholder="未入力なら全メンバー"
                />
              </label>
              <label>
                P1を許可するロールID
                <input
                  value={guild.p1RoleIds.join(", ")}
                  onChange={(e) => updateGuildRoles("p1RoleIds", e.target.value)}
                  placeholder="未入力なら制限なし"
                />
              </label>
            </div>
          ) : (
            <p className="empty-copy">Discordサーバーが接続されていません。</p>
          )}
        </section>
        <section className="settings-card">
          <div className="settings-card-heading">
            <div className="settings-icon amber">
              <CircleAlert size={19} />
            </div>
            <div>
              <h2>チケット管理</h2>
              <p>初期状態では追加設定不要の内部チケットを使用します。</p>
            </div>
          </div>
          <div className="form-grid">
            <label>
              プロバイダー
              <select
                value={settings.tenant.ticketProvider}
                onChange={(e) => updateTenant("ticketProvider", e.target.value)}
              >
                <option value="internal">SCIM Internal</option>
                <option value="github">GitHub Issues</option>
                <option value="jira" disabled>
                  Jira（連携アダプター準備中）
                </option>
              </select>
            </label>
            <label>
              内部チケット接頭辞
              <input
                value={settings.tenant.ticketProject ?? ""}
                onChange={(e) => updateTenant("ticketProject", e.target.value)}
                placeholder="INC"
              />
            </label>
          </div>
          {settings.tenant.ticketProvider === "github" && (
            <div className="form-grid integration-fields">
              <label>
                Fine-grained token
                <input
                  type="password"
                  autoComplete="new-password"
                  value={githubToken}
                  onChange={(event) => setGithubToken(event.target.value)}
                  placeholder={github.configured ? "変更する場合のみ入力" : "github_pat_..."}
                />
              </label>
              <label>
                リポジトリOwner
                <input
                  value={github.owner}
                  onChange={(event) => updateGithub("owner", event.target.value)}
                  placeholder="Studio-CLAP"
                />
              </label>
              <label>
                リポジトリ名
                <input
                  value={github.repository}
                  onChange={(event) => updateGithub("repository", event.target.value)}
                  placeholder="incident-tracker"
                />
              </label>
              <label>
                Projects Organization（任意）
                <input
                  value={github.projectOwner ?? ""}
                  onChange={(event) =>
                    updateGithub("projectOwner", event.target.value.trim() || null)
                  }
                  placeholder="未入力ならリポジトリOwner"
                />
              </label>
              <label>
                Projects番号（任意）
                <input
                  type="number"
                  min="1"
                  value={github.projectNumber ?? ""}
                  onChange={(event) =>
                    updateGithub(
                      "projectNumber",
                      event.target.value ? Number(event.target.value) : null,
                    )
                  }
                  placeholder="7"
                />
              </label>
              <label>
                ラベル
                <input
                  value={github.labels.join(", ")}
                  onChange={(event) => updateGithub("labels", roleIds(event.target.value))}
                  placeholder="incident, production"
                />
              </label>
              <label>
                Assignee（任意）
                <input
                  value={github.assignees.join(", ")}
                  onChange={(event) => updateGithub("assignees", roleIds(event.target.value))}
                  placeholder="octocat"
                />
              </label>
            </div>
          )}
          <div className="info-banner">
            {settings.tenant.ticketProvider === "github" ? (
              <Github size={18} />
            ) : (
              <ShieldCheck size={18} />
            )}
            <div>
              <strong>
                {settings.tenant.ticketProvider === "github"
                  ? github.configured
                    ? "GitHub連携設定済み"
                    : "GitHub連携を設定"
                  : "内部チケット方式で稼働中"}
              </strong>
              <p>
                {settings.tenant.ticketProvider === "github"
                  ? "Tokenは暗号化して保存され、画面へ再表示されません。Issues: writeとProjects: writeを付与してください。"
                  : "追加の外部アカウントや認証情報なしで利用できます。"}
              </p>
            </div>
          </div>
        </section>
      </div>
      {error && <p className="settings-error">{error}</p>}
    </main>
  );
}

export function App() {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [unauthorized, setUnauthorized] = useState(false);
  const [tenantId, setTenantId] = useState(() => localStorage.getItem("scim:tenant") ?? "");
  const [view, setView] = useState<View>(() => {
    const requested = new URLSearchParams(window.location.search).get("view");
    return requested === "reports" || requested === "settings" || requested === "members"
      ? requested
      : "incidents";
  });
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [incidents, setIncidents] = useState<IncidentListItem[]>([]);
  const [filters, setFilters] = useState({ status: "all", severity: "all", query: "" });
  const [selected, setSelected] = useState<string | null>(() => {
    const match = window.location.pathname.match(/^\/incidents\/([^/]+)$/);
    return match?.[1] ?? null;
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loadingTable, setLoadingTable] = useState(false);

  useEffect(() => {
    api
      .me()
      .then((result) => {
        setMe(result);
        const valid = result.tenants.find((tenant) => tenant.id === tenantId)?.id;
        setTenantId(valid ?? result.tenants[0]?.id ?? "");
      })
      .catch((error) => {
        if (error instanceof ApiError && error.status === 401) setUnauthorized(true);
        else console.error(error);
      });
  }, []);

  const refresh = useCallback(async () => {
    if (!tenantId) return;
    setLoadingTable(true);
    try {
      const [dashboard, list] = await Promise.all([
        api.dashboard(tenantId),
        api.incidents(tenantId, filters),
      ]);
      setSummary(dashboard);
      setIncidents(list.incidents);
    } finally {
      setLoadingTable(false);
    }
  }, [tenantId, filters]);

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(refresh, 30_000);
    return () => window.clearInterval(timer);
  }, [refresh]);
  useEffect(() => {
    if (tenantId) localStorage.setItem("scim:tenant", tenantId);
  }, [tenantId]);

  const tenant = useMemo(
    () => me?.tenants.find((item) => item.id === tenantId) ?? me?.tenants[0],
    [me, tenantId],
  );
  if (unauthorized) return <LoginScreen />;
  if (!me) return <LoadingScreen />;
  if (me.tenants.length === 0) return <NoTenantAccess me={me} />;
  if (!tenant) return <LoadingScreen />;

  const selectIncident = (incidentId: string | null) => {
    setSelected(incidentId);
    window.history.replaceState(null, "", incidentId ? `/incidents/${incidentId}` : "/");
  };
  const changeView = (nextView: View) => {
    setView(nextView);
    if (nextView !== "incidents") {
      window.history.replaceState(null, "", `/?view=${nextView}`);
    } else if (!selected) window.history.replaceState(null, "", "/");
  };

  return (
    <div className="app-shell">
      <Sidebar
        view={view}
        onView={changeView}
        mobileOpen={mobileOpen}
        onClose={() => setMobileOpen(false)}
      />
      <div className="workspace">
        <Header
          me={me}
          tenant={tenant}
          onTenant={setTenantId}
          onMenu={() => setMobileOpen(true)}
          onIncident={(incidentId) => {
            changeView("incidents");
            selectIncident(incidentId);
          }}
        />
        {view === "settings" ? (
          <SettingsView tenant={tenant} />
        ) : view === "members" ? (
          <MembersView tenant={tenant} currentUserId={me.user.id} />
        ) : view === "reports" ? (
          <ReportsView tenant={tenant} />
        ) : (
          <main className="content">
            <div className="page-heading">
              <div>
                <p className="eyebrow">INCIDENT OVERVIEW</p>
                <h1>障害一覧</h1>
                <p>チーム全体のインシデントをリアルタイムに追跡します。</p>
              </div>
              <div className="sync-indicator">
                <span />
                <span>30秒ごとに同期</span>
              </div>
            </div>
            <section className="metrics-grid" aria-label="障害サマリー">
              <MetricCard
                label="対応中"
                value={summary?.open ?? "—"}
                note="未解決のインシデント"
                tone="neutral"
                icon={<Clock3 size={20} />}
              />
              <MetricCard
                label="重大障害"
                value={summary?.critical ?? "—"}
                note="現在進行中のP1"
                tone="critical"
                icon={<CircleAlert size={20} />}
              />
              <MetricCard
                label="今月の復旧"
                value={summary?.resolvedThisMonth ?? "—"}
                note="クローズ済みを含む"
                tone="success"
                icon={<Check size={20} />}
              />
              <MetricCard
                label="平均復旧時間"
                value={formatDuration(summary?.medianResolutionMinutes ?? null)}
                note="解決済み障害から算出"
                tone="purple"
                icon={<Gauge size={20} />}
              />
            </section>
            <section className="incident-panel">
              <div className="panel-toolbar">
                <div className="search-box">
                  <Search size={18} />
                  <input
                    aria-label="障害を検索"
                    placeholder="チケット、件名、サービスを検索"
                    value={filters.query}
                    onChange={(e) => setFilters({ ...filters, query: e.target.value })}
                  />
                </div>
                <div className="filter-group">
                  <ListFilter size={17} />
                  <select
                    aria-label="重要度で絞り込み"
                    value={filters.severity}
                    onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
                  >
                    <option value="all">すべての重要度</option>
                    {incidentSeverities.map((severity) => (
                      <option key={severity} value={severity}>
                        {severity} · {severityLabels[severity]}
                      </option>
                    ))}
                  </select>
                  <select
                    aria-label="ステータスで絞り込み"
                    value={filters.status}
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  >
                    <option value="all">すべての状態</option>
                    {incidentStatuses.map((status) => (
                      <option key={status} value={status}>
                        {statusLabels[status]}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className={`table-wrap ${loadingTable ? "is-loading" : ""}`}>
                <IncidentTable incidents={incidents} onSelect={selectIncident} />
              </div>
              <footer className="panel-footer">
                <span>{incidents.length}件を表示</span>
                <span>
                  <span className="live-pulse" /> D1と同期済み
                </span>
              </footer>
            </section>
          </main>
        )}
      </div>
      <DetailDrawer
        tenantId={tenant.id}
        incidentId={selected}
        onClose={() => selectIncident(null)}
        onUpdated={refresh}
      />
    </div>
  );
}
